import { UseFormReturnType } from '@mantine/form';
import {
  EmailServerServiceCheckIntegrationParams,
  IMailServer
} from '@/services/api/emailServerApiArea/types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStyles } from '@/containers/pages/filials/edit/styles';
import { Button, Checkbox, SimpleGrid, TextInput } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { emailServerApiArea } from '@/services/api/emailServerApiArea';
import { showNotification } from '@mantine/notifications';

interface Props {
  form: UseFormReturnType<IMailServer>;
}

const ApiForm = (props: Props) => {
  const { form } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const isMobile = useMediaQuery('(max-width: 992px)');
  const [connection, setConnection] = React.useState<boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleCheckConnection = () => {
    const Integration = form.values.Integration;
    const params: EmailServerServiceCheckIntegrationParams = {
      smtp: Integration.Smtp,
      ssh: Integration.Ssh,
      api: Integration.Api,
      type: Integration.Type
    };

    setIsLoading(true);
    emailServerApiArea()
      .EmailServerCheckIntegration(params)
      .then(() => {
        setConnection(true);
        showNotification({
          title: t('servers.api.connect.title'),
          message: t('servers.api.connect.success'),
          autoClose: true,
          color: 'green'
        });
      })
      .catch(() => {
        setConnection(false);
        showNotification({
          title: t('servers.api.connect.title'),
          message: t('servers.api.connect.failed'),
          autoClose: true,
          color: 'warning'
        });
      })
      .finally(() => setIsLoading(false));
  };

  // ✅ напрямую читаем из form.values каждый раз
  const isDisabled = useMemo(() => {
    const { Login, Password, Host } = form.values.Integration.Api ?? {};

    if (!Login || !Password || !Host) {
      setConnection(undefined);
      return true;
    }

    return false;
  }, [
    form.values.Integration?.Api?.Login,
    form.values.Integration?.Api?.Password,
    form.values.Integration?.Api?.Host
  ]);

  const color = useMemo(() => {
    if (connection === undefined) {
      return 'primary';
    }

    return connection ? 'green' : 'red';
  }, [connection]);

  return (
    <SimpleGrid cols={1} mt={16} w={isMobile ? '100%' : '50%'}>
      <TextInput
        w="100%"
        className={classes.textFieldInput}
        placeholder={t('servers.api.Login')}
        label={t('servers.api.Login')}
        withAsterisk
        {...form.getInputProps('Integration.Api.Login')}
      />
      <TextInput
        w="100%"
        className={classes.textFieldInput}
        placeholder={t('servers.api.Password')}
        label={t('servers.api.Password')}
        withAsterisk
        {...form.getInputProps('Integration.Api.Password')}
      />
      <TextInput
        w="100%"
        className={classes.textFieldInput}
        placeholder={t('servers.api.Host')}
        label={t('servers.api.Host')}
        withAsterisk
        {...form.getInputProps('Integration.Api.Host')}
      />
      <Checkbox
        className={classes.checkbox}
        label={t('servers.api.HasLdapAuth')}
        size="xs"
        {...form.getInputProps('Integration.Api.HasLdapAuth')}
      />
      <Button
        type="submit"
        loading={isLoading}
        color={color}
        w={isMobile ? '100%' : 'fit-content'}
        disabled={isDisabled}
        onClick={handleCheckConnection}
      >
        {t('servers.api.connect.button')}
      </Button>
    </SimpleGrid>
  );
};

export default ApiForm;
